import React, {useCallback, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useEventQueue} from "./hooks/use-event-queue.js";
import {useSearchParams} from "react-router";
import {GisContext} from "./context/gis-context.jsx";
import {getAssetPath} from "../../../utils/get-asset-path.js";
import GisUtils from "./utils/gis-utils.jsx";
import gisUtils from "./utils/gis-utils.jsx";
import GisConfig from "./components/gis-config.jsx";
import GisCard from "./components/gis-card.jsx";
import GisMenu from "./components/gis-menu.jsx";
import {WazeSubType, WazeType} from "../../../utils/waze-types.js";
import {ControlPosition, MapControl} from "@vis.gl/react-google-maps";
import Button from "../../../components/button/button.jsx";
import GisModalVideoCameras from "./components/gis-modal-video-cameras.jsx";
import {FaRotate} from "react-icons/fa6";
import GisModalDispatch from "./components/gis-modal-dispatch.jsx";
import {useSpiderfier} from "../../../hooks/use-spiderfier.js";
import GisGeocoding from "./components/gis-geocoding.jsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

let eventSelected, targetSelected, firstTime = true, findItemId = false, tempMarker, findTempMarker = false;
let selectionRectangle, selectionListeners = [];

const mapGeoLayers = new Map();
const mapGeoItems = new Map();
const mapMarkers = new Map();
const wazeMarkers = new Map();
let videoCamerasToOpenGlobal = [];

const mapControlState = {
    neighborhoodStreets: GisUtils.getFromLocalStorage("neighborhoodStreets"),
    pointsOfInterest: GisUtils.getFromLocalStorage("pointsOfInterest"),
    trafficStatus: GisUtils.getFromLocalStorage("trafficStatus"),
    trafficLayer: undefined
}

const Gis = () => {
    const [searchParams] = useSearchParams();

    const {menu, changeMenu, setTarget, geoLayerChecked, setGeoLayerChecked, setDrawingMode, drawingMode, map, coreLib, markerLib, mapLib, setTempCardParams, tempCardActionRef, tempCardParams} = useContext(GisContext);

    const currentBounds = useRef(undefined);

    const [overlay, setOverlay] = useState(undefined);
    const [configIsOpen, setConfigIsOpen] = useState(false);
    const [geocodingIsOpen, setGeocodingIsOpen] = useState(false);
    const [eventsCount, setEventsCount] = useState({});

    const [mapControls, setMapControls] = useState(mapControlState);
    const [videoCamerasToOpen, setVideoCamerasToOpen] = useState([]);
    const [modalVideoCameras, setModalVideoCameras] = useState(false);

    const [mapEvents, setMapEvents] = useState(new Map());

    const oms = useSpiderfier(map, mapMarkers, mapGeoItems);

    const { addEvents } = useEventQueue(async (event) => {
        await loadMarker(event);
    });

    useEffect(() => {
        if (!map || !coreLib || !markerLib || !mapLib) return;

        currentBounds.current = new coreLib.LatLngBounds();
        const overlayView = new mapLib.OverlayView();

        overlayView.draw = function() {};
        overlayView.setMap(map);

        setOverlay(overlayView);

        coreLib.event.addListener(map, 'click', () => {
            handleDetails(targetSelected, null, false)
            oms.unspiderfy();
        });

        if (setTempCardParams && (searchParams.has('latLng') || searchParams.has('id'))) {
            setTempCardParams({latLng: searchParams.get("latLng"), id: searchParams.get("id")});
        }

        map.setOptions({
            styles: [
                {
                    "elementType": 'labels', "stylers": [
                        {
                            "visibility": mapControls.neighborhoodStreets ? 'on' : 'off'
                        }
                    ]
                },
                {
                    "featureType": "poi",
                    "elementType": "all",
                    "stylers": [
                        {
                            "visibility": mapControls.pointsOfInterest ? 'on' : 'off'
                        }
                    ]
                },
                {
                    "featureType": "road",
                    "elementType": "labels.text.fill",
                    "stylers": [
                        {
                            "visibility": mapControls.neighborhoodStreets ? 'on' : 'off'
                        }
                    ]
                },
            ]
        });

        const trafficLayer = new mapLib.TrafficLayer();
        if (mapControls.trafficStatus) trafficLayer.setMap(map);

        setMapControls(prev => ({...prev, trafficLayer }));

        getEvents();
    }, [map, coreLib, markerLib, mapLib]);

    useEffect(() => {
        if (drawingMode) {
            selectCamerasEvent();
        } else {
            stopCamerasSelection();
        }
    }, [drawingMode]);

    const getEvents = () => {
        const source = new EventSource(`${API_KEEPER}/gis`);

        source.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (Array.isArray(data) && data.length > 0) {
                addEvents(data);
            }
        };

        source.onerror = (err) => {
            console.error("Error to load events", err);
        };
    };

    const selectCamerasEvent = () => {
        let startLatLng = null;

        map.setOptions({draggable: false, draggableCursor: "crosshair"});

        const onMouseDown = e => {
            startLatLng = e.latLng;
            selectionRectangle?.setMap(null);
            selectionRectangle = new mapLib.Rectangle({
                map,
                clickable: false,
                fillColor: "#1d4ed8",
                fillOpacity: 0.15,
                strokeColor: "#1d4ed8",
                strokeWeight: 2,
                bounds: new coreLib.LatLngBounds(startLatLng, startLatLng)
            });
        };

        const onMouseMove = e => {
            if (!startLatLng) return;

            const bounds = new coreLib.LatLngBounds();
            bounds.extend(startLatLng);
            bounds.extend(e.latLng);
            selectionRectangle.setBounds(bounds);
        };

        const onMouseUp = () => {
            if (!startLatLng) return;

            const bounds = selectionRectangle.getBounds();
            startLatLng = null;
            selectionRectangle.setMap(null);
            selectionRectangle = undefined;

            handleSelectedCameras(bounds);
        };

        selectionListeners = [
            map.addListener("mousedown", onMouseDown),
            map.addListener("mousemove", onMouseMove),
            map.addListener("mouseup", onMouseUp)
        ];
    }

    const stopCamerasSelection = () => {
        selectionListeners.forEach(listener => listener.remove());
        selectionListeners = [];
        selectionRectangle?.setMap(null);
        selectionRectangle = undefined;
        map?.setOptions({draggable: true, draggableCursor: null});
    }

    const handleSelectedCameras = (bounds) => {
        videoCamerasToOpenGlobal = [...mapMarkers]
            .filter(([, marker]) => marker.type === "vms" && marker.visible)
            .filter(([, marker]) => bounds.contains(new coreLib.LatLng(marker.latitude, marker.longitude)))
            .map(([, marker]) => marker);

        if (videoCamerasToOpenGlobal.length === 0) return;

        if (videoCamerasToOpenGlobal.length >= 9) {
            setVideoCamerasToOpen(videoCamerasToOpenGlobal);
            setModalVideoCameras(true);
            setDrawingMode(false);
            return;
        }

        const cameras = videoCamerasToOpenGlobal.map(camera => {
            let id = camera.id;
            if (id.includes('video-camera')) {
                id = camera.details.description.id;
            }

            return {
                id: camera.id,
                isVms: id.includes('video-camera'),
                name: camera.title,
                vmsId: camera.details?.vmsConfigurationId
            }
        });

        gisUtils.openMosaicCameras(cameras);
    }

    const loadMarker = useCallback((events) => {
        let fitBound = true;
        const tempMap = new Map();

        for (const event of events) {
            if (event.type === 'point' || event.type === 'zone') {
                event.type_ = 'geolayer';
            }

            const eventId = event.id.toUpperCase();
            if (event?.type_ === 'geolayer' && mapEvents.has(eventId) || (event.status === 'deleted')) {
                if (event.type === 'point' || event.id.includes('point')) {
                    [...mapGeoItems].filter(([, marker]) => marker.id_ === eventId).forEach(([, marker]) => {
                        marker.setMap(null);
                        mapGeoItems.delete(marker.id);
                        oms.removeMarker(marker.id);
                    });

                } else if (event.type === 'zone' || event.id.includes('zone')) {
                    mapGeoLayers.get(eventId).forEach(element => {
                        if (!element.type_) {
                            element.setMap(null)
                        }
                    });

                    mapGeoLayers.delete(eventId);
                }

                if (event.status === 'deleted' && (event.id.includes('zone') || event.id.includes('point'))) {
                    const newMenu = {...menu};
                    const newChecked = geoLayerChecked.filter(item => item !== event.id.toUpperCase());

                    newMenu.point.show = newChecked.some(item => item.includes("POINT"));
                    newMenu.zone.show = newChecked.some(item => item.includes("ZONE"));

                    changeMenu(newMenu);
                    setGeoLayerChecked(newChecked);
                }
            }

            if (event.remove || (event.status === 'deleted') || !validateLatLng(event)) {
                tempMap.delete(event.id)
            } else {
                tempMap.set(event.id, event);
            }

            if (event.remove) {
                if (mapMarkers.has(event.id)) {
                    mapMarkers.get(event.id).setMap(null);
                    mapMarkers.delete(event.id);
                    oms.removeMarker(event.id);
                }

                continue;
            } else if (!event.details) {
                console.error(event);
                continue;
            } else if (!validateLatLng(event)) {
                continue;
            }

            let marker;
            if (mapMarkers.has(event.id)) {
                marker = mapMarkers.get(event.id);

                const newMarker = createMarker(event, map);
                marker.latitude = newMarker.latitude;
                marker.longitude = newMarker.longitude;
                marker.type = newMarker.type;
                marker.status = newMarker.status;
                marker.icon = newMarker.icon;
                marker.iconStandard = newMarker.iconStandard;
                marker.iconZoom1 = newMarker.iconZoom1;
                marker.iconZoom2 = newMarker.iconZoom2;
                marker.iconTree = newMarker.iconTree;
                marker.title = newMarker.title;
                marker.alarm = newMarker.alarm;
                marker.details = newMarker.details;

                marker.setPosition(new coreLib.LatLng(event.latitude, event.longitude));
                marker.setIcon(marker.icon);

                mapMarkers.set(event.id, marker);
            } else {
                if (event.type === 'point') {
                    event.details.layers.forEach((element, i) => {
                        let newEvent = event;
                        newEvent.latitude = element.coordinates[0];
                        newEvent.longitude = element.coordinates[1];
                        const marker_ = new markerLib.Marker(createMarker(newEvent, map));
                        marker_.type_ = 'geolayer';

                        marker_.alarm = event.alarm;
                        marker_.title = element.name;
                        marker_.id_ = event.id;
                        marker_.id = element.id;
                        marker_.description = element.description;
                        marker = marker_;

                        if (menu.point?.showAll) marker_.setVisible(true);
                        mapGeoItems.set(element.id, marker_);

                        if (marker_.visible) {
                            currentBounds.current.extend(new coreLib.LatLng(marker_.latitude, marker_.longitude));
                        }

                        oms.addMarker(marker_);
                    });

                } else if (event.type === 'zone') {
                    event.details.layers.forEach(element => {
                        const coord = addPolygon(element.coordinates, element.color, event.id, map);

                        if (menu.zone?.showAll) coord.polygon.setVisible(true);

                        if (coord.polygon.visible) {
                            for (const el of coord.coordinates_) {
                                currentBounds.current.extend(new coreLib.LatLng(el.lat, el.lng));
                            }
                        }
                    });

                } else {
                    marker = new markerLib.Marker(createMarker(event, map));

                    if (marker.visible) {
                        currentBounds.current.extend(new coreLib.LatLng(marker.latitude, marker.longitude));
                    }

                    if (marker.type === "waze") wazeMarkers.set(marker.id, marker);
                }

                if (event?.type_ === 'geolayer') {
                    for (const [, element] of mapGeoItems.entries()) {
                        coreLib.event.clearListeners(element, 'click');
                        element.addListener('click', () => {
                            oms.spiderfy(element.id, () => {
                                handleDetails(element.type, element, false);
                                element.setAnimation(markerLib.Animation.BOUNCE);
                                element.setZIndex(99);
                            });
                        });
                    }
                    continue;
                }

                mapMarkers.set(event.id, marker);
            }

            mapEvents.set(event.id, event);

            coreLib.event.clearListeners(marker, 'click');
            marker.addListener('click', () => {
                oms.spiderfy(marker.id, () => {
                    handleDetails(event.type, event, false);
                    marker.setAnimation(markerLib.Animation.BOUNCE);
                    marker.setZIndex(99);
                });
            });

            if (tempCardParams && tempCardParams?.latLng  && !findTempMarker) {
                const latLng = tempCardParams.latLng.split(",");
                if (parseFloat(latLng[0]) === event.latitude && parseFloat(latLng[1]) === event.longitude) {
                    marker.setVisible(true);
                    marker.setAnimation(markerLib.Animation.BOUNCE);
                    marker.setZIndex(99);

                    map.setCenter(marker.getPosition());
                    handleDetails(marker.type, event);

                    if (tempMarker) {
                        tempMarker.setMap(null);
                        tempMarker = undefined;
                    }

                    findTempMarker = true;
                    fitBound = false;
                }
            }

            oms.addMarker(marker);
        }

        setMapEvents(prev => (new Map([...prev, ...tempMap])));

        if (firstTime) {
            if (tempCardParams && tempCardParams?.latLng && !findTempMarker) {
                const latLng = tempCardParams.latLng.split(",");

                tempMarker = new markerLib.Marker({
                    position: new coreLib.LatLng(latLng[0], latLng[1]),
                    icon: getAssetPath('img/markers-gis/icon.png'),
                    zIndex: 10,
                    animation: markerLib.Animation.BOUNCE,
                    map,
                    id: "temp-marker"
                });

                map.setZoom(18);
                map.setCenter(tempMarker.getPosition());

                oms.addMarker(tempMarker);
                fitBound = false;
            }

            if (fitBound) {
                map.fitBounds(currentBounds.current);
            }
        }

        if (!findItemId && searchParams.has('item_id')) {
            const marker = mapMarkers.get(searchParams.get('item_id'));

            if (marker && tempMap.has(searchParams.get('item_id'))) {
                marker.setVisible(true);
                marker.setAnimation(markerLib.Animation.BOUNCE);
                marker.setZIndex(99);

                map.setCenter(marker.getPosition());
                handleDetails(marker.type, tempMap.get(marker.id));

                map.setZoom(18);
                findItemId = true;
            }
        }

        firstTime = false;

        if (targetSelected && eventSelected) {
            let newEvent = mapEvents.get(eventSelected.id);

            if (newEvent?.remove || !newEvent) newEvent = null;

            if (eventSelected.type === 'point' && mapGeoItems.has(eventSelected.id)) {
                newEvent = mapGeoItems.get(eventSelected.id);
                mapGeoItems.get(eventSelected.id).setAnimation(markerLib.Animation.BOUNCE);
            }

            if (newEvent && mapMarkers.has(newEvent.id)) {
                mapMarkers.get(newEvent.id).setAnimation(markerLib.Animation.BOUNCE);
            }

            handleDetails(targetSelected, newEvent, false, false);
        }

        setEventsCount(GisUtils.getEventsCount(mapEvents));
    }, [mapEvents, markerLib, tempCardParams])

    const validateLatLng = (event) => {
        let ret = true;
        if (!event.latitude || !event.longitude) {
            ret = false;
        }

        const isLatitude = () => isFinite(event.latitude) && Math.abs(event.latitude) <= 90;
        const isLongitude = () => isFinite(event.longitude) && Math.abs(event.longitude) <= 180;

        if (!isLatitude() || !isLongitude()) {
            console.error(`Evento "${event.title}" possui latitude ou longitude inválida.`);
            ret = false;
        }

        return ret;
    }

    const createMarker = (event, map) => {
        if (event.remove) return;

        const size = localStorage.getItem("marker-size") ?? "iconZoom2";

        const {show} = menu[event.type];

        const location = new coreLib.LatLng(event.latitude, event.longitude)
        const markerIcon = preparePathsToMarkers(event);
        let visible = show;

        if (event.type === "point") visible = geoLayerChecked.includes(event.id);

        let title = event.title;
        if (event.type === "waze") {
            title = WazeType[event.details.type] + (event.details?.subType ? " - " + WazeSubType[event.details.subType] : "");
        }

        return {
            position: location,
            icon: event.type !== "point" ? markerIcon[size] : markerIcon.iconStandard,
            iconStandard: markerIcon.iconStandard,
            iconZoom1: event.type !== "point" ? markerIcon.iconZoom1 : markerIcon.iconStandard,
            iconZoom2: event.type !== "point" ? markerIcon.iconZoom2 : markerIcon.iconStandard,
            rotation: event.details ? event.details.cameraAngle : undefined,
            title,
            zIndex: GisUtils.getPriority(event.type, event.alarm),
            latitude: event.latitude,
            longitude: event.longitude,
            map,
            type: event.type,
            status: event.status,
            visible,
            alarm: event.alarm,
            selected: false,
            id: event.id,
            iconTree: event.icon,
            date: event.date,
            details: event.details ?? []
        };
    }

    const preparePathsToMarkers = (event) => {
        const type = event.type;
        let icon = event.icon;
        const pathDefault = 'img/markers-gis';

        if (event.type === "contactid" && event.details?.fireAlarm) {
            icon = `fire_alarm-offline`;
        }

        let iconStandard = {url: getAssetPath(`${pathDefault}/${type}/${icon}.png`), labelOrigin: new coreLib.Point(13, 40)}
        let iconZoom1 = {url: getAssetPath(`${pathDefault}/${type}/${icon}-zoom1.png`), labelOrigin: new coreLib.Point(16, 50)}
        let iconZoom2 = {url: getAssetPath(`${pathDefault}/${type}/${icon}-zoom2.png`), labelOrigin: new coreLib.Point(20, 65)}

        if (type === 'vms') {
            if (event.details?.color) {
                const {cameraAngle, color} = event.details;
                const iconColor = gisUtils.vmsCameraIcon(color)

                iconStandard = getAssetPath(`img/video-cameras/${iconColor}/${iconColor}.png`);
                iconZoom1 = getAssetPath(`img/video-cameras/${iconColor}/${iconColor}-zoom1.png`);
                iconZoom2 = getAssetPath(`img/video-cameras/${iconColor}/${iconColor}-zoom2.png`);

                const rotation = cameraAngle;
                if (rotation) {
                    let roundedRotation = Math.round(rotation / 15) * 15;
                    if (rotation === 0 || rotation === 360) roundedRotation = 0;

                    iconStandard = getAssetPath(`img/video-cameras/${iconColor}/rotations/${roundedRotation}.png`);
                    iconZoom1 = getAssetPath(`img/video-cameras/${iconColor}/rotations-zoom1/${roundedRotation}.png`);
                    iconZoom2 = getAssetPath(`img/video-cameras/${iconColor}/rotations-zoom2/${roundedRotation}.png`);
                }
            } else {
                iconStandard = getAssetPath(`${pathDefault}/${type}/marker-camera.png`);
                iconZoom1 = getAssetPath(`${pathDefault}/${type}/marker-camera-zoom1.png`);
                iconZoom2 = getAssetPath(`${pathDefault}/${type}/marker-camera-zoom2.png`);

                const rotation = event.details.cameraAngle;
                if (rotation) {
                    let roundedRotation = Math.round(rotation / 15) * 15;
                    if (rotation === 0 || rotation === 360) roundedRotation = 0;

                    iconStandard = getAssetPath(`${pathDefault}/${type}/marker-camera-rotation/${roundedRotation}.png`);
                    iconZoom1 = getAssetPath(`${pathDefault}/${type}/marker-camera-rotation-zoom1/${roundedRotation}.png`);
                    iconZoom2 = getAssetPath(`${pathDefault}/${type}/marker-camera-rotation-zoom2/${roundedRotation}.png`);
                }
            }

        }

        if (type === 'point') {
            iconStandard = getAssetPath('img/markers/customLayers/normal/' + icon);

            return {iconStandard}
        }

        if (event.alarm) {
            iconStandard = getAssetPath(`${pathDefault}/${type}/${icon}/alarm.png`);
            iconZoom1 = getAssetPath(`${pathDefault}/${type}/${icon}/alarm-zoom1.png`);
            iconZoom2 = getAssetPath(`${pathDefault}/${type}/${icon}/alarm-zoom2.png`);
        }

        return {iconStandard, iconZoom1, iconZoom2};
    }

    const addPolygon = (coordinates, color, eventId, map) => {
        let coordinates_ = [];
        let index = 0;
        let latLng = {};

        coordinates.forEach((coordinate) => {
            if (index === 0) {
                latLng.lat = Number(coordinate);
            } else if (index === 1) {
                latLng.lng = Number(coordinate);
            }

            index++;
            if (index === 2) {
                coordinates_.push(latLng);
                index = 0;
                latLng = {};
            }
        });

        const polygon = new mapLib.Polygon({
            paths: coordinates_,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillOpacity: 0.35,
            strokeColor: color,
            fillColor: color,
            id_: eventId,
            visible: false
        });

        polygon.setMap(map);

        if (geoLayerChecked.includes(eventId)) polygon.setVisible(true);

        mapGeoLayers.get(eventId) ? mapGeoLayers.get(eventId).push(polygon) : mapGeoLayers.set(eventId, [polygon]);

        return {polygon, coordinates_};
    }

    const handleDetails = (target_, event = null, changeTarget = true, changeEvent = true) => {
        const isSameSelection = (target_ === targetSelected && event === eventSelected);

        targetSelected = (isSameSelection && changeTarget) ? null : target_;
        eventSelected = (isSameSelection && changeEvent) ? null : event;

        setConfigIsOpen(false);
        setGeocodingIsOpen(false);
        setTarget({selected: targetSelected, eventSelected});
    }

    const toggleConfig = () => {
        targetSelected = null;
        eventSelected = null;

        setConfigIsOpen(!configIsOpen);
        setGeocodingIsOpen(false);
        setTarget({selected: targetSelected, eventSelected});
    }

    const toggleGeocoding = () => {
        targetSelected = null;
        eventSelected = null;

        setGeocodingIsOpen(!geocodingIsOpen);
        setConfigIsOpen(false);
        setTarget({selected: targetSelected, eventSelected});
    }

    useImperativeHandle(
        tempCardActionRef,
        () => ({ click: () => {
            if (tempMarker) {
                tempMarker.setMap(null);
                tempMarker = undefined;
            }

            setTempCardParams({});
        }})
    );

    return (
        <>
            <MapControl position={ControlPosition.LEFT_TOP}>
                <div id="gis-menu" className="flex">
                    <GisMenu
                        handleDetails={handleDetails}
                        toggleConfig={toggleConfig}
                        configIsOpen={configIsOpen}
                        toggleGeocoding={toggleGeocoding}
                        geocodingIsOpen={geocodingIsOpen}
                        eventsCount={eventsCount}
                    />

                    <GisCard
                        handleDetails={handleDetails}
                        markers={mapMarkers}
                        geoLayers={mapGeoLayers}
                        geoItems={mapGeoItems}
                        events={mapEvents}
                        map={map}
                        overlay={overlay}
                        eventsCount={eventsCount}
                        selectCamerasEvent={selectCamerasEvent}
                    />

                    <GisConfig
                        isOpen={configIsOpen}
                        map={map}
                        markers={mapMarkers}
                        geoLayers={mapGeoLayers}
                        geoItems={mapGeoItems}
                        mapControls={mapControls}
                        setMapControls={setMapControls}
                        toggleConfig={toggleConfig}
                        events={mapEvents}
                    />

                    <GisGeocoding
                        map={map}
                        toggleGeocoding={toggleGeocoding}
                        isOpen={geocodingIsOpen}
                    />
                </div>
            </MapControl>

            <MapControl position={ControlPosition.TOP_RIGHT}>
                <div className={"bg-[#ffa500] text-white h-[40px] rounded py-[5px] px-[10px] mt-[11px] w-full"} hidden={!drawingMode}>
                    <div className="flex items-center justify-between w-full">
                        <span className={"me-2 text-[16px]"}>Clique e arraste para selecionar.</span>
                        <Button onClick={() => setDrawingMode(false)}>
                            Cancelar seleção
                        </Button>
                    </div>
                </div>
            </MapControl>

            <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <div className="p-[10px]">
                    <button className={"w-[40px] h-[40px] border border-[#bdbdbd8c] bg-white flex items-center justify-center cursor-pointer"} onClick={() => GisUtils.resetZoom(map, mapGeoItems, mapMarkers)}>
                        <FaRotate size={20}/>
                    </button>
                </div>
            </MapControl>

            <GisModalVideoCameras
                modalVideoCameras={modalVideoCameras}
                setModalVideoCameras={setModalVideoCameras}
                setVideoCamerasToOpen={setVideoCamerasToOpen}
                videoCamerasToOpen={videoCamerasToOpen}
            />

            <GisModalDispatch />
        </>
    );
};

export default Gis;